import { useMemo } from 'react';

import { usePulseHeartbeat, useRecordPulseOutcome } from './store/api';
import type { PulseTransport } from './usePulseController';

async function resolveOperation<T>(
  operation: { unwrap: () => Promise<T>; abort: () => void; reset: () => void },
  signal: AbortSignal,
): Promise<T> {
  const abort = () => operation.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  try {
    return await operation.unwrap();
  } finally {
    signal.removeEventListener('abort', abort);
    // Pulse responses belong to the mounted controller, never an idle shared cache.
    operation.reset();
  }
}

/** Authenticated generated RTK endpoints; no alternate URL or hand-written API request. */
export function usePulseTransport(): PulseTransport {
  const [heartbeat] = usePulseHeartbeat();
  const [outcome] = useRecordPulseOutcome();
  return useMemo(
    () => ({
      heartbeat: signal => {
        signal.throwIfAborted();
        return resolveOperation(heartbeat(), signal);
      },
      outcome: (pulseId, request, signal) => {
        signal.throwIfAborted();
        return resolveOperation(outcome({ pulseId, pulseOutcomeRequest: request }), signal);
      },
    }),
    [heartbeat, outcome],
  );
}
